import React, { useMemo } from 'react';
import {
  Grid, Card, CardContent, Typography, Stack, Avatar, Chip,
  Box, Divider, List, ListItem, ListItemText, ListItemIcon
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SchoolIcon from '@mui/icons-material/School';
import BookIcon from '@mui/icons-material/Book';

const EducationalScheduleStaff = ({ staff, schedules }) => {
  // Calculate statistics for each staff member
  const staffStats = useMemo(() => {
    return staff.map(member => {
      const memberSchedules = schedules.filter(s => s.teacher === member.id);
      const totalHours = memberSchedules.reduce((sum, s) => sum + (s.duration_minutes || 0), 0) / 60;
      const uniqueClasses = new Set(memberSchedules.map(s => s.school_class_name).filter(Boolean));
      const uniqueSubjects = new Set(memberSchedules.map(s => s.subject_name).filter(Boolean));
      const uniqueDays = new Set(memberSchedules.map(s => s.day_of_week).filter(Boolean));

      return {
        ...member,
        totalSchedules: memberSchedules.length,
        totalHours: totalHours.toFixed(1),
        classesCount: uniqueClasses.size,
        subjectsCount: uniqueSubjects.size,
        daysCount: uniqueDays.size,
        classes: Array.from(uniqueClasses),
        subjects: Array.from(uniqueSubjects)
      };
    });
  }, [staff, schedules]);

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  if (staffStats.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          لا يوجد موظفين مرتبطين بالجدول
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {staffStats.map((member) => (
        <Grid item xs={12} md={6} lg={4} key={member.id}>
          <Card elevation={2} sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Stack spacing={2}>
                {/* Header */}
                <Stack useFlexGap direction="row" spacing={2} alignItems="center">
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: 56,
                      height: 56,
                      fontSize: '1.2rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {getInitials(`${member.first_name} ${member.last_name}`)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {member.first_name} {member.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {member.employee_type_display || member.job_title || 'معلم'}
                    </Typography>
                  </Box>
                </Stack>

                <Divider />

                {/* Statistics */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'primary.light', borderRadius: 2 }}>
                      <Typography variant="h5" fontWeight="bold" color="primary.main">
                        {member.totalSchedules}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        حصة
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.light', borderRadius: 2 }}>
                      <Typography variant="h5" fontWeight="bold" color="success.main">
                        {member.totalHours}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ساعة
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight="bold" color="info.main">
                        {member.classesCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        صف
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight="bold" color="warning.main">
                        {member.subjectsCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        مادة
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight="bold" color="secondary.main">
                        {member.daysCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        يوم
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Divider />

                {/* Classes */}
                {member.classes.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      الصفوف:
                    </Typography>
                    <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap">
                      {member.classes.map((className, idx) => (
                        <Chip
                          key={idx}
                          size="small"
                          label={className}
                          icon={<SchoolIcon />}
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Subjects */}
                {member.subjects.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      المواد:
                    </Typography>
                    <Stack useFlexGap direction="row" spacing={1} flexWrap="wrap">
                      {member.subjects.map((subject, idx) => (
                        <Chip
                          key={idx}
                          size="small"
                          label={subject}
                          icon={<BookIcon />}
                          color="secondary"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default EducationalScheduleStaff;
